import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Info, Link2, Pencil, Plus, Smartphone, Trash2 } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  SMS_LINES_KEY,
  useConnectSmsWebhooksMutation,
  useDeleteSmsLineMutation,
  useRemoveSmsPhoneMutation,
  useSaveSmsLineMutation,
  useSmsLinesQuery,
} from '@/client/api/inbox/smsLines';
import type { SmsLine, SmsLineInput, SmsPhone } from '@/client/api/inbox/smsLines';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/** A phone that checked in within this long is shown as online. */
const ONLINE_MINUTES = 10;

const online = (p?: SmsPhone) => Boolean(p?.lastSeen && moment().diff(moment(p.lastSeen), 'minutes') < ONLINE_MINUTES);
const phoneName = (p?: SmsPhone) => (p ? p.name || p.id : 'Unknown phone');
const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

type Draft = SmsLineInput & { isNew: boolean };

const blank = (device_id = ''): Draft => ({
  isNew: true,
  id: '',
  label: '',
  phone: '',
  device_id,
  sim_number: null,
  enabled: true,
  default: false,
});

/**
 * SMS lines: the SIMs in our Android phones that SMS is sent from and
 * arrives on, plus the Hubtel sender (one-way). A phone is added on the phone
 * itself (the SMSGate app, signed in to our relay); here it is named, split
 * into lines per SIM, and pointed at our webhook.
 */
const SmsLinesPage: React.FC = () => {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useSmsLinesQuery();
  const canManage = useGate(PERMISSIONS.messaging.manage).allowed;
  const save = useSaveSmsLineMutation();
  const remove = useDeleteSmsLineMutation();
  const removePhone = useRemoveSmsPhoneMutation();
  const connect = useConnectSmsWebhooksMutation();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'line' | 'phone'; id: string; name: string } | null>(null);

  const lines = data?.lines ?? [];
  const phones = data?.phones ?? [];
  const phoneById = (id?: string | null) => phones.find((p) => p.id === id);
  const unassigned = phones.filter((p) => !lines.some((l) => l.device_id === p.id));
  const refresh = () => qc.invalidateQueries({ queryKey: SMS_LINES_KEY });
  const fail = (e: unknown, fallback: string) => toast.error((e as Error)?.message || fallback);

  const edit = (l: SmsLine) =>
    setDraft({
      isNew: false,
      id: l.id,
      label: l.label,
      phone: l.phone || '',
      device_id: l.device_id || '',
      sim_number: l.sim_number,
      enabled: l.enabled,
      default: l.default,
    });

  const submit = () => {
    if (!draft) return;
    const { isNew, ...input } = draft;
    const id = isNew ? slug(input.id || input.label) : input.id;
    save.mutate(
      { ...input, id },
      {
        onSuccess: () => {
          toast.success(isNew ? 'Line added' : 'Line saved');
          setDraft(null);
          refresh();
        },
        onError: (e) => fail(e, 'The line was not saved'),
      },
    );
  };

  const runConfirm = () => {
    if (!confirm) return;
    const done = {
      onSuccess: () => {
        toast.success(confirm.kind === 'line' ? 'Line removed' : 'Phone removed from the relay');
        setConfirm(null);
        refresh();
      },
      onError: (e: unknown) => fail(e, 'That did not work'),
    };
    if (confirm.kind === 'line') remove.mutate({ id: confirm.id }, done);
    else removePhone.mutate({ device_id: confirm.id }, done);
  };

  const connectPhones = () =>
    connect.mutate(undefined, {
      onSuccess: (res) => {
        const added = res?.data?.added ?? [];
        toast.success(added.length ? `Connected: ${added.join(', ')}` : 'Already connected');
      },
      onError: (e) => fail(e, 'The phones were not connected'),
    });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SMS lines</h1>
            <p className="text-sm text-gray-500 mt-1">
              The numbers SMS is sent from and replies arrive on. Hubtel sends one-way from &ldquo;FeelyFeely&rdquo;; phone
              lines take replies.
            </p>
          </div>
          <div className="flex gap-2">
            <PermissionButton code={PERMISSIONS.messaging.manage} variant="outline" onClick={connectPhones} disabled={connect.isPending}>
              <Link2 /> Connect phones
            </PermissionButton>
            <PermissionButton code={PERMISSIONS.messaging.manage} onClick={() => setDraft(blank())}>
              <Plus /> Add line
            </PermissionButton>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {isLoading && <p className="text-sm text-gray-500">Loading SMS lines...</p>}
        {isError && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">Could not load SMS lines: {(error as Error)?.message || 'unknown error'}</p>
          </div>
        )}
        {data?.relay_error && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">The phone relay cannot be reached, so phone status is unknown.</p>
              <p className="mt-1">{data.relay_error}</p>
            </div>
          </div>
        )}

        {data && (
          <section className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Line</th>
                  <th className="px-4 py-3 font-medium">Number</th>
                  <th className="px-4 py-3 font-medium">Phone / SIM</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((l) => {
                  const p = phoneById(l.device_id);
                  return (
                    <tr key={l.id} className={l.enabled ? '' : 'text-gray-400'}>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{l.label}</span>
                        {l.default && (
                          <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                            Default for replies
                          </span>
                        )}
                        {!l.two_way && <p className="text-xs text-gray-500">One-way: replies are not collected</p>}
                      </td>
                      <td className="px-4 py-3">{l.phone || (l.two_way ? 'Not entered' : 'Sender ID')}</td>
                      <td className="px-4 py-3">
                        {l.two_way ? `${phoneName(p)} · ${l.sim_number ? `SIM ${l.sim_number}` : 'any SIM'}` : 'Hubtel'}
                      </td>
                      <td className="px-4 py-3">
                        {!l.enabled ? (
                          'Off'
                        ) : !l.two_way ? (
                          'On'
                        ) : online(p) ? (
                          <span className="inline-flex items-center gap-1 text-green-700">
                            <CheckCircle className="w-4 h-4" /> Online
                          </span>
                        ) : (
                          <span className="text-amber-700">
                            {p?.lastSeen ? `Last seen ${moment(p.lastSeen).fromNow()}` : 'Phone not found'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {l.two_way && canManage && (
                          <>
                            <Button variant="ghost" size="sm" aria-label={`Edit ${l.label}`} onClick={() => edit(l)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Remove ${l.label}`}
                              onClick={() => setConfirm({ kind: 'line', id: l.id, name: l.label })}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        {data && (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Phones on the relay</h2>
            {phones.length === 0 && !data.relay_error && (
              <p className="text-sm text-gray-500">No phones have signed in yet.</p>
            )}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {phones.map((p) => (
                <div key={p.id} className="bg-white rounded-lg border border-gray-200 p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-900 break-all">{phoneName(p)}</span>
                  </div>
                  <p className="text-xs text-gray-500 break-all">ID {p.id}</p>
                  <p className={`text-sm ${online(p) ? 'text-green-700' : 'text-amber-700'}`}>
                    {online(p) ? 'Online' : p.lastSeen ? `Last seen ${moment(p.lastSeen).fromNow()}` : 'Never seen'}
                  </p>
                  {canManage && (
                    <div className="flex gap-2 pt-1">
                      {unassigned.includes(p) && (
                        <Button size="sm" variant="outline" onClick={() => setDraft(blank(p.id))}>
                          <Plus className="w-4 h-4" /> Make a line
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600"
                        onClick={() => setConfirm({ kind: 'phone', id: p.id, name: phoneName(p) })}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              <Info className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-semibold">Adding a phone</p>
                <p>
                  Install the FeelyFeely SMS app (it already points at our relay), turn on Cloud server and sign in with
                  the relay account (ops keystore, <code className="font-mono">ff-api-external</code>:{' '}
                  <code className="font-mono">SMSGATE_USERNAME</code>). In the app's Settings &gt; Webhooks, paste the
                  signing key (<code className="font-mono">SMSGATE_WEBHOOK_SECRET</code>); without it its messages are
                  refused. Turn off battery optimisation and &ldquo;Manage app if unused&rdquo;, and leave it charging.
                  It shows up here within a minute; make it a line, then press Connect phones.
                </p>
              </div>
            </div>
          </section>
        )}
      </div>

      <Dialog open={Boolean(draft)} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{draft?.isNew ? 'Add SMS line' : 'Edit SMS line'}</DialogTitle>
            <DialogDescription>One SIM in one phone. People who text this number land in the inbox on this line.</DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="sms-line-label">Name</Label>
                <Input
                  id="sms-line-label"
                  value={draft.label}
                  placeholder="Accra sales 1"
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sms-line-phone">Number</Label>
                <Input
                  id="sms-line-phone"
                  value={draft.phone || ''}
                  placeholder="+233..."
                  onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="sms-line-device">Phone</Label>
                  <select
                    id="sms-line-device"
                    value={draft.device_id}
                    onChange={(e) => setDraft({ ...draft, device_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="">Choose a phone</option>
                    {phones.map((p) => (
                      <option key={p.id} value={p.id}>
                        {phoneName(p)}
                      </option>
                    ))}
                    {draft.device_id && !phoneById(draft.device_id) && <option value={draft.device_id}>{draft.device_id}</option>}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sms-line-sim">SIM</Label>
                  <select
                    id="sms-line-sim"
                    value={draft.sim_number ?? ''}
                    onChange={(e) => setDraft({ ...draft, sim_number: e.target.value ? Number(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="">Any (single-SIM phone)</option>
                    <option value="1">SIM 1</option>
                    <option value="2">SIM 2</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.enabled !== false} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
                On (can be chosen for sending)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={Boolean(draft.default)} onChange={(e) => setDraft({ ...draft, default: e.target.checked })} />
                Default for replies when a conversation has no line yet
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!draft?.label.trim() || !draft?.device_id || save.isPending}>
              {save.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(confirm)} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.kind === 'line' ? 'Remove line?' : 'Remove phone from the relay?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === 'line'
                ? `"${confirm?.name}" stops being offered for sending. Its conversations stay in the inbox.`
                : `"${confirm?.name}" stops sending and receiving until it signs in again. Its lines stay, so they can be pointed at a replacement.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirm} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SmsLinesPage;
